import { Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ScreenshotService } from './screenshot.service';
import { CaptureRect, CaptureResult } from './screenshot.models';
import { DocumentPickerComponent } from '../document-picker/document-picker.component';
import { CurrentModuleContextService } from 'src/app/services/core/current-module-context.service';
import { DocumentService } from '../../document/document.service';

/**
 * Floating action popup shown next to the captured region. Two actions only:
 * Copy (clipboard) and Save to Document (opens the document picker preselected
 * to the current module, then uploads into the chosen folder via the service).
 * The component fires the calls and passes the anchoring rectangle through to
 * CSS positioning.
 */
@Component({
  selector: 'app-screenshot-toolbar',
  templateUrl: './screenshot-toolbar.component.html',
  styleUrls: ['./screenshot-toolbar.component.scss'],
  standalone: false,
})
export class ScreenshotToolbarComponent {
  @Input() rect: CaptureRect | null = null;

  isCopying = false;

  constructor(
    private screenshotService: ScreenshotService,
    private dialog: MatDialog,
    private moduleContext: CurrentModuleContextService,
    private documentService: DocumentService
  ) {}

  /**
   * Position the toolbar just below the selection when there's room,
   * otherwise stick it above.
   */
  get positionStyles(): { [k: string]: string } {
    if (!this.rect) return {};

    const gap = 8;
    const toolbarWidth = 260;
    const toolbarHeight = 44;

    const preferBelow =
      this.rect.y + this.rect.height + toolbarHeight + gap <= window.innerHeight;
    const top = preferBelow
      ? this.rect.y + this.rect.height + gap
      : Math.max(gap, this.rect.y - toolbarHeight - gap);

    // Anchor left edge to selection, but keep the toolbar inside the viewport.
    const rawLeft = this.rect.x;
    const maxLeft = window.innerWidth - toolbarWidth - gap;
    const left = Math.max(gap, Math.min(rawLeft, maxLeft));

    return { top: `${top}px`, left: `${left}px` };
  }

  async onCopy(): Promise<void> {
    if (this.isCopying) return;
    this.isCopying = true;
    try {
      await this.screenshotService.copyToClipboard();
    } finally {
      this.isCopying = false;
    }
  }

  onSave(): void {
    // Grab the capture, then dismiss the overlay BEFORE opening the picker.
    // The screenshot overlay is z-index 10000; the picker dialog lives in the CDK
    // overlay container (z-index 1000, its own stacking context), so if we leave
    // the overlay up the dialog renders behind it and the first click lands on the
    // overlay backdrop — which calls cancel() and wipes the capture. Keeping the
    // blob locally and clearing the overlay first avoids that race.
    const capture = this.screenshotService.currentCapture;
    if (!capture) return;

    // Scope the picker to the page we're on:
    //  - Detail page (moduleId + referenceId): open INSIDE the record's own folder,
    //    e.g. on Customer CUST-16 the picker starts in that customer's folder so
    //    the screenshot can be saved there (or in one of its subfolders).
    //  - List page (moduleId only): the module root (all folders).
    //  - Dashboard / login / settings (neither): default first-module view.
    const ctx = this.moduleContext.getContext();
    const moduleId = ctx.moduleId ?? undefined;
    const referenceId = ctx.referenceId ?? undefined;

    this.screenshotService.cancel();

    if (moduleId == null || referenceId == null) {
      this.openPicker(capture, moduleId, referenceId);
      return;
    }

    // Resolve the record's own folder so the picker can open directly inside it.
    // Same call the picker makes for a record scope: module + attachable_id with
    // no folder_id returns that record's top-level folder.
    this.documentService
      .getDocuments({ page: 1, module_id: moduleId, attachable_id: referenceId }, false)
      .subscribe({
        next: (res: any) => {
          const list = res?.data?.data?.documents?.list ?? [];
          const recordFolder = list.find((item: any) => item?.type === 'folder');
          this.openPicker(capture, moduleId, referenceId, recordFolder?.id);
        },
        // No folder yet (or the lookup failed) — fall back to the record-scoped view.
        error: () => this.openPicker(capture, moduleId, referenceId),
      });
  }

  /**
   * Open the document picker in folder-select mode and upload the capture into
   * whatever the user picks. `folderId` (when known) makes it open inside that
   * folder rather than showing it as a row to drill into.
   */
  private openPicker(
    capture: CaptureResult,
    moduleId?: number | string,
    referenceId?: number | string,
    folderId?: number | string
  ): void {
    const ref = this.dialog.open(DocumentPickerComponent, {
      width: '860px',
      maxWidth: '95vw',
      panelClass: 'document-picker-dialog',
      autoFocus: false,
      data: {
        multiple: false,
        selectable: 'folder',
        moduleId,
        referenceId,
        folderId,
        enableCustomFileName: true,
        defaultFileName: 'screenshot.png',
      },
    });
    ref.afterClosed().subscribe((result) => {
      if (!result) return;
      this.screenshotService.saveToFolder(result, capture);
    });
  }
}
